/**
 * Формирование пулов материала.
 *
 * Пять пулов соответствуют пяти долям смеси. Материал не пересекается: слово
 * не может одновременно быть слабым и контрольным, иначе Transfer Guard
 * потерял бы смысл (ТЗ §7.6).
 */

import type { MasteryStat, MasteryUnit } from '@type2x/shared-contracts';
import { WEAK_THRESHOLD, toMasteryUnitId } from '@type2x/shared-contracts';
import { getWords } from '@type2x/corpora';
import {
  computePriority,
  isDue,
  rankUnits,
  type LearningConfig,
  type PriorityContext,
} from '@type2x/learning-engine';
import { fingerForCharacter, getLayout } from '@type2x/keyboard-layouts';
import { handOfFinger, type Hand } from '@type2x/shared-contracts';
import type { CandidatePools } from '@type2x/exercise-generator';

import type { SkillSnapshot } from '../contracts/practice.js';

/** Сколько единиц держать в каждом пуле: с запасом относительно квоты. */
const POOL_SIZE = 24;

/** Определяет ведущую руку единицы по первому символу. */
export function handOfUnit(unit: MasteryUnit): Hand | null {
  const layout = getLayout(unit.layoutId);
  const first = [...unit.content][0];
  if (first === undefined) return null;
  const finger = fingerForCharacter(layout, first);
  return finger === null ? null : handOfFinger(finger);
}

/** Строит контекст расчёта приоритета из снимка навыка. */
export function priorityContext(snapshot: SkillSnapshot, nowMs: number): PriorityContext {
  const control = new Set(snapshot.seenInControl);
  return {
    nowMs,
    leftHandShare: snapshot.leftHandShare,
    handOf: handOfUnit,
    seenInControl: (unit) => control.has(unit.id),
  };
}

/** Новые слова языка, ещё не встречавшиеся пользователю. */
function freshUnits(snapshot: SkillSnapshot, exclude: ReadonlySet<string>): MasteryUnit[] {
  return getWords(snapshot.language, { minLength: 2, limit: 120 })
    .map((word) => ({
      id: toMasteryUnitId(`word:${word.text}`),
      kind: 'word' as const,
      content: word.text,
      language: snapshot.language,
      layoutId: snapshot.layoutId,
      realWorldFrequency: word.frequency,
    }))
    .filter((unit) => !exclude.has(unit.id))
    .slice(0, POOL_SIZE);
}

/**
 * Контрольный материал: слова, не входящие ни в один тренировочный пул.
 *
 * Берутся из хвоста частотного списка, чтобы гарантированно не пересекаться
 * с тем, что пользователь уже тренировал.
 */
function transferUnits(snapshot: SkillSnapshot, exclude: ReadonlySet<string>): MasteryUnit[] {
  const all = getWords(snapshot.language, { minLength: 3 });
  return all
    .slice(Math.floor(all.length / 2))
    .map((word) => ({
      id: toMasteryUnitId(`control:${word.text}`),
      kind: 'word' as const,
      content: word.text,
      language: snapshot.language,
      layoutId: snapshot.layoutId,
      realWorldFrequency: word.frequency,
    }))
    .filter((unit) => !exclude.has(toMasteryUnitId(`word:${unit.content}`)))
    .slice(0, POOL_SIZE);
}

/** Куда отнести единицу: слабое, пора повторить или поддержание автоматизма. */
function classify(stat: MasteryStat, nowMs: number): 'weak' | 'due' | 'maintenance' {
  if (stat.masteryScore < WEAK_THRESHOLD) return 'weak';
  if (isDue(stat, nowMs)) return 'due';
  return 'maintenance';
}

/** Собирает все пять пулов. */
export function buildPools(
  snapshot: SkillSnapshot,
  config: LearningConfig,
  nowMs: number,
): CandidatePools {
  const context = priorityContext(snapshot, nowMs);
  const known = Object.values(snapshot.units);

  const ranked = rankUnits(
    known
      .filter((unit) => snapshot.stats[unit.id] !== undefined)
      .map((unit) => computePriority(unit, snapshot.stats[unit.id]!, config, context)),
  );

  const weak: MasteryUnit[] = [];
  const due: MasteryUnit[] = [];
  const maintenance: MasteryUnit[] = [];

  for (const result of ranked) {
    const target = classify(snapshot.stats[result.unit.id]!, nowMs);
    const bucket = target === 'weak' ? weak : target === 'due' ? due : maintenance;
    if (bucket.length < POOL_SIZE) bucket.push(result.unit);
  }

  const usedIds = new Set([...weak, ...due, ...maintenance].map((unit) => unit.id));
  const knownIds = new Set(known.map((unit) => unit.id));

  const pools: CandidatePools = {
    weak,
    due: due.length > 0 ? due : weak,
    fresh: freshUnits(snapshot, usedIds),
    maintenance: maintenance.length > 0 ? maintenance : known.slice(0, POOL_SIZE),
    transfer: transferUnits(snapshot, knownIds),
  };

  // Пустой пул сделал бы упражнение короче заказанного: подставляем ближайший
  // осмысленный источник материала.
  return {
    weak: pools.weak.length > 0 ? pools.weak : pools.fresh,
    due: pools.due.length > 0 ? pools.due : pools.fresh,
    fresh: pools.fresh.length > 0 ? pools.fresh : pools.transfer,
    maintenance: pools.maintenance.length > 0 ? pools.maintenance : pools.fresh,
    transfer: pools.transfer.length > 0 ? pools.transfer : pools.fresh,
  };
}
